import React, { useRef } from 'react';
import { Box } from '@mui/material';

import type { QueueViewKey } from '../../../types/encounterWork';

import { HOME_QUEUE_PANEL_ID, homeQueueTabId, nextTabIndex } from './homeQueueTabs';
import type { HomeQueueTile } from './homeQueueTiles';
import { QueueTile } from './QueueTile';

/**
 * Home's summary strip (GDN-4085): the five queue tiles as an ARIA tablist,
 * and the panel below it that shows the selected queue's table.
 *
 * Presentational. The container decides which tile is selected and keeps it
 * in the URL; the strip reports the choice and moves focus. One tab stop for
 * the whole strip (the selected tile, or the first before any is), and the
 * arrow keys, Home and End move between the tiles, selecting as they go:
 * every table is already loaded, so there is nothing to wait for.
 *
 * Layout is CSS grid alone: five across from `lg`, three from `md`, and two
 * below that, so the tiles wrap rather than shrink past their names.
 */

export interface HomeQueueStripProps {
  /** Always five, in priority order. */
  tiles: readonly HomeQueueTile[];
  onSelect: (key: QueueViewKey) => void;
}

export const HomeQueueStrip: React.FC<HomeQueueStripProps> = ({ tiles, onSelect }) => {
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = tiles.findIndex(tile => tile.selected);
  const tabStop = selectedIndex >= 0 ? selectedIndex : 0;

  const onKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const target = nextTabIndex(event.key, index, tiles.length);
    if (target === null) return;
    event.preventDefault();
    tabs.current[target]?.focus();
    onSelect(tiles[target].queue.key);
  };

  return (
    <Box
      aria-label="Queues"
      data-testid="home-queue-strip"
      role="tablist"
      sx={{
        display: 'grid',
        gap: 1.75,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))',
          lg: 'repeat(5, minmax(0, 1fr))',
        },
      }}
    >
      {tiles.map((tile, index) => (
        <QueueTile
          key={tile.queue.key}
          ref={(element: HTMLButtonElement | null) => {
            tabs.current[index] = element;
          }}
          id={homeQueueTabId(tile.queue.key)}
          onKeyDown={onKeyDown(index)}
          onSelect={() => onSelect(tile.queue.key)}
          panelId={HOME_QUEUE_PANEL_ID}
          tabIndex={index === tabStop ? 0 : -1}
          tile={tile}
        />
      ))}
    </Box>
  );
};

export interface HomeQueuePanelProps {
  /** The selected queue, which labels the panel; null before one is chosen. */
  selectedKey: QueueViewKey | null;
  children: React.ReactNode;
}

/** The tab panel: whatever the selected queue shows, labelled by its tile. */
export const HomeQueuePanel: React.FC<HomeQueuePanelProps> = ({ selectedKey, children }) => (
  <Box
    aria-labelledby={selectedKey ? homeQueueTabId(selectedKey) : undefined}
    data-testid="home-queue-panel"
    id={HOME_QUEUE_PANEL_ID}
    role="tabpanel"
    sx={{
      borderRadius: '10px',
      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
    }}
    tabIndex={0}
  >
    {children}
  </Box>
);
